import React, { useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";

const OP = "Patrons Nest Ltd · Company no. 15011254 · Enterprise Building, Rope Walk, Lincoln, England, LN6 7DQ, United Kingdom";
const UPDATED = "Last updated: 11 September 2026";

function Doc({ title, children }) {
  return (
    <div className="max-w-3xl mx-auto prose-psl" data-testid="policy-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Policies</div>
      <h1 className="font-head font-black text-3xl md:text-4xl text-green mt-1">{title}</h1>
      <p className="text-xs text-ink/40 mt-1">{UPDATED} · Operated by {OP}</p>
      <div className="mt-6 space-y-4 text-ink/80 text-[15px] leading-relaxed [&_h2]:font-head [&_h2]:font-bold [&_h2]:text-green [&_h2]:text-lg [&_h2]:mt-6 [&_a]:text-green [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
        {children}
      </div>
    </div>
  );
}

const AFFIL = (
  <p className="bg-cream border border-gold/50 rounded-sm p-3 text-sm">
    <b>Independent site — no official affiliation.</b> Premier League Zimbabwe (premierleaguezw.com) is an independent
    football information and fan website. It is not operated, endorsed or licensed by the Zimbabwe Premier Soccer League,
    ZIFA, the Football Association Premier League Limited, or any club. Club names, crests and competition marks remain the
    property of their owners and appear only for identification and editorial reporting.
  </p>
);

export function PrivacyPolicy() {
  return (
    <Doc title="Privacy Policy">
      {AFFIL}
      <h2>1. Who we are</h2>
      <p>This policy applies to premierleaguezw.com and any official app or newsletter we operate. {OP} is the data controller.</p>
      <h2>2. Data we collect</h2>
      <ul><li>Identity/contact: name, email, username, country or city if given</li><li>Account: login details, preferences, favourite clubs</li><li>User content: comments, votes, messages</li><li>Technical: IP address, device/browser, pages viewed, referring URL</li><li>Usage & marketing preferences</li></ul>
      <p>We do not ask for special-category data. Please do not send it to us.</p>
      <h2>3. How we use it & legal bases (UK GDPR)</h2>
      <p>To run, secure and improve the site; manage accounts; reply to support; measure audience; and display advertising including personalised ads where you consent. Legal bases: contract, legitimate interests, consent (non-essential cookies, personalised ads, marketing), and legal obligation.</p>
      <h2>4. Advertising & Google</h2>
      <p>This site uses Google AdSense and/or other Google advertising services. Google and third-party vendors may use cookies, web beacons and IP addresses to serve, measure and personalise ads based on your visits here and to other sites. See <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">how Google uses data</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's privacy policy</a>. Opt out of personalised ads at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">adssettings.google.com</a>. Manage choices via our Cookie settings.</p>
      <h2>5. Sharing & transfers</h2>
      <p>We share data with hosting, security, analytics, email and support providers on our instructions, and with Google for analytics/advertising. We do not sell your personal data. The Services run from the UK; data may be processed in the UK, EEA and US (e.g. by Google) under approved safeguards.</p>
      <h2>6. Retention & your rights</h2>
      <p>We keep data only as long as needed. You may access, correct, delete, restrict, object, withdraw consent or request portability — email <a href="mailto:legal@premierleaguezw.com">legal@premierleaguezw.com</a>. You may complain to the UK ICO (ico.org.uk).</p>
      <h2>7. Children — 16+ only</h2>
      <p>The Services are strictly for people aged 16 or over. We do not knowingly collect data from anyone under 16. Parents/guardians: email legal@premierleaguezw.com with subject "Under-16 data request".</p>
      <h2>8. Contact</h2>
      <p>Privacy & legal: <a href="mailto:legal@premierleaguezw.com">legal@premierleaguezw.com</a> · Admin: <a href="mailto:admin@premierleaguezw.com">admin@premierleaguezw.com</a></p>
    </Doc>
  );
}

export function CookiePolicy() {
  return (
    <Doc title="Cookie Policy">
      <p>We use cookies and similar technologies (pixels, local storage) on premierleaguezw.com.</p>
      <h2>Categories</h2>
      <ul><li><b>Strictly necessary</b> — security, login, consent storage</li><li><b>Analytics</b> — understand usage (e.g. Google Analytics)</li><li><b>Functionality</b> — remember language, theme, favourite club</li><li><b>Advertising</b> — measure and show ads, including Google AdSense</li></ul>
      <h2>Google advertising cookies</h2>
      <p>If you allow advertising cookies, Google may set cookies to serve, measure and personalise ads. See <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google ad technologies</a> and <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Ad Settings</a>.</p>
      <h2>Consent</h2>
      <p>On your first visit we show a banner. Necessary cookies always run; analytics, functionality and advertising run only if you accept. Change your choice any time via "Cookie settings" in the footer. Contact: <a href="mailto:legal@premierleaguezw.com">legal@premierleaguezw.com</a>.</p>
    </Doc>
  );
}

export function TermsPage() {
  return (
    <Doc title="Terms of Use">
      {AFFIL}
      <h2>Acceptance & age</h2>
      <p>By using the Services you agree to these terms, the Privacy Policy and the Cookie Policy. You must be 16 or over to use the Services and to create an account.</p>
      <h2>Accounts & your content</h2>
      <p>Keep your password confidential; you are responsible for activity on your account. If you post content you grant us a non-exclusive, worldwide licence to use it to operate and promote the Services. Do not post unlawful, defamatory, hateful, infringing or spam content.</p>
      <h2>Advertising</h2>
      <p>The Services may display third-party ads, including Google ads, labelled where required. Do not click ads except as a genuine user, and do not ask others to click ads.</p>
      <h2>Scores, fixtures & tools</h2>
      <p>Scores, tables, line-ups and predictors are for information and entertainment, may be delayed or wrong, and have no cash value unless a specific promotion says so.</p>
      <h2>Liability & law</h2>
      <p>The Services are provided "as is". Nothing limits liability that cannot be limited under English law. These terms are governed by the laws of England and Wales. Contact: <a href="mailto:contact@premierleaguezw.com">contact@premierleaguezw.com</a>.</p>
    </Doc>
  );
}

export function AboutPage() {
  return (
    <Doc title="About Premier League Zimbabwe">
      {AFFIL}
      <p>Premier League Zimbabwe (premierleaguezw.com) is an independent football website for Zimbabwean football fans at home and in the diaspora. We publish news, fixtures, scores, tables, features, a ZiG-priced fantasy game and community discussion — free, with no paywall.</p>
      <h2>Who operates the site</h2>
      <p>{OP}. Corporate email: <a href="mailto:admin@patronsnest.com">admin@patronsnest.com</a>.</p>
      <h2>Age</h2>
      <p>The site is strictly for users aged 16 and over.</p>
      <h2>Contact</h2>
      <p>General: <a href="mailto:contact@premierleaguezw.com">contact@premierleaguezw.com</a> · Support: <a href="mailto:support@premierleaguezw.com">support@premierleaguezw.com</a> · Full <a href="/contact">contact page</a>.</p>
    </Doc>
  );
}

export function DisclaimerPage() {
  return (
    <Doc title="Disclaimer">
      {AFFIL}
      <h2>Information only</h2>
      <p>Articles, scores, tables, line-ups, injury notes and statistics are for general information and may be late, incomplete or wrong. Official results and rules sit with the competition organiser.</p>
      <h2>No advice · advertising · links</h2>
      <p>Nothing here is financial, legal, medical or betting advice. Third-party ads (including Google ads) are the advertiser's responsibility. We are not responsible for external websites. User comments are their own. The site is not for anyone under 16.</p>
    </Doc>
  );
}

export function ChildrensPolicy() {
  return (
    <Doc title="Children's Policy">
      <p><b>Premier League Zimbabwe is strictly not for children.</b> You must be 16 or older to use the site, create an account, comment, enter quizzes, or submit personal data.</p>
      <h2>No child accounts</h2>
      <p>We do not offer child accounts or under-16 versions of the site, and we do not knowingly collect personal data from anyone under 16. Because the site is for users 16+, we may display advertising, including Google ads, in line with our Privacy and Cookie policies. We do not use a child's data for remarketing.</p>
      <h2>If a person under 16 has used the site</h2>
      <p>A parent or guardian should email <a href="mailto:legal@premierleaguezw.com">legal@premierleaguezw.com</a> with subject "Under-16 data request" and we will delete the data as soon as reasonably possible.</p>
    </Doc>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message"); return; }
    try { await client.post("/contact", form); setSent(true); toast.success("Message sent — we aim to reply within 10 working days."); }
    catch { toast.error("Could not send. Please email contact@premierleaguezw.com"); }
  };
  const inp = "w-full border border-pslborder rounded-sm px-3 py-2 bg-white focus:border-gold outline-none";
  return (
    <Doc title="Contact us">
      <p>Premier League Zimbabwe is operated by {OP}.</p>
      <div className="grid md:grid-cols-2 gap-8 not-prose">
        <div>
          <h2 className="font-head font-bold text-green text-lg mb-2">Email us</h2>
          <ul className="text-sm space-y-1">
            <li>General: <a className="text-green underline" href="mailto:contact@premierleaguezw.com">contact@premierleaguezw.com</a></li>
            <li>Support (bugs, login): <a className="text-green underline" href="mailto:support@premierleaguezw.com">support@premierleaguezw.com</a></li>
            <li>Help: <a className="text-green underline" href="mailto:help@premierleaguezw.com">help@premierleaguezw.com</a></li>
            <li>Legal & privacy: <a className="text-green underline" href="mailto:legal@premierleaguezw.com">legal@premierleaguezw.com</a></li>
          </ul>
          <p className="text-xs text-ink/50 mt-3">We aim to reply within 10 working days. Not for emergencies. Age 16+ only.</p>
        </div>
        {sent ? (
          <div className="bg-green/10 text-green rounded-sm p-4 text-sm font-bold" data-testid="contact-sent">Thanks — your message has been sent. We'll reply within 10 working days.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <input className={inp} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            <input className={inp} type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            <input className={inp} placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
            <textarea className={inp} rows={5} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            <button className="w-full py-2.5 bg-gold text-ink font-black rounded-sm hover:bg-green hover:text-cream transition-colors" data-testid="contact-submit">Send message</button>
          </form>
        )}
      </div>
    </Doc>
  );
}
